import React, { FC, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import i18next from 'i18next';
import { useDispatch } from 'react-redux';
import NotFoundPage from '~/pages/NotFoundPage';
import TechnicalErrorPage from '~/pages/TechnicalErrorPage';
import { LoginPageProps } from '~/pages/LoginPage/LoginPage';
import { ChangePasswordPageProps } from '~/pages/ChangePasswordPage/ChangePasswordPage';
import { ChangePasswordConfirmPageProps } from '~/pages/ChangePasswordConfirmPage/ChangePasswordConfirmPage';
import AxiosSetup from '~/components/AxiosSetup';
import { contentRequested } from './App.actions';
import CustomPageLoader from '../components/CustomPageLoader';
import MainLayout from '../layouts/MainLayout/MainLayout';
import FormLayout from '../layouts/FormLayout/FormLayout';
import MobileLayout from '../layouts/MobileLayout/MobileLayout';

const LoginPage = (props: LoginPageProps) => <CustomPageLoader loader={() => import('../pages/LoginPage')} {...props} />;
const ChangePasswordPage = (props: ChangePasswordPageProps) => <CustomPageLoader loader={() => import('../pages/ChangePasswordPage')} {...props} />;
const ChangePasswordConfirmPage = (props: ChangePasswordConfirmPageProps) => <CustomPageLoader loader={() => import('../pages/ChangePasswordConfirmPage')} {...props} />;
const LogoffPage = () => <CustomPageLoader loader={() => import('../pages/LogoffPage')} />;
const LoggingTestPage = () => <CustomPageLoader loader={() => import('../pages/LoggingTestPage')} />;
const AccessErrorPage = () => <CustomPageLoader loader={() => import('~/pages/AccessErrorPage')} />;

const App: FC = () => {
  const dispatch = useDispatch();
  const lang = i18next.language;
  const queryParams = new URLSearchParams(window.location.search);
  const fromURI = queryParams.get('fromURI');
  const isMobileIndicatorStored = (sessionStorage.getItem('mobileDeviceMode') === 'true');
  useEffect(() => {
    dispatch(contentRequested(lang));
  }, [dispatch, lang]);
  return (
    <MainLayout>
      <AxiosSetup />
      <Routes>
        <Route
          path="/mobile"
          element={
            <MobileLayout>
              {fromURI === null ? <LogoffPage /> : <LoginPage mobileDeviceMode />}
            </MobileLayout>
          }
        />
        <Route
          path="/"
          element={
            <FormLayout>
              {/* eslint-disable-next-line no-nested-ternary */}
              {fromURI === null ? <LogoffPage /> :
                isMobileIndicatorStored ? <Navigate to={{ pathname: '/mobile', search: `?fromURI=${fromURI}` }} /> : <LoginPage mobileDeviceMode={false} />}
            </FormLayout>
          }
        />

        <Route
          path="/mobile/change-password"
          element={
            <MobileLayout>
              <ChangePasswordPage mobileDeviceMode />
            </MobileLayout>
          }
        />
        <Route
          path="/change-password"
          element={
            <FormLayout>
              {isMobileIndicatorStored ?
                <Navigate to={{ pathname: '/mobile/change-password' }} /> :
                <ChangePasswordPage mobileDeviceMode={false} />}
            </FormLayout>
          }
        />

        <Route
          path="/mobile/change-password-confirm"
          element={
            <MobileLayout>
              <ChangePasswordConfirmPage mobileDeviceMode />
            </MobileLayout>
          }
        />
        <Route
          path="/change-password-confirm"
          element={
            <FormLayout>
              {isMobileIndicatorStored ?
                <Navigate to={{ pathname: '/mobile/change-password-confirm' }} /> :
                <ChangePasswordConfirmPage mobileDeviceMode={false} />}
            </FormLayout>
          }
        />

        <Route path="/log-off" element={<LogoffPage />} />

        <Route
          path="/logging-test"
          element={
            <FormLayout>
              <LoggingTestPage />
            </FormLayout>
          }
        />

        <Route
          path="/access-error"
          element={
            <FormLayout>
              <AccessErrorPage />
            </FormLayout>
          }
        />

        <Route
          path="/not-found"
          element={
            <FormLayout>
              <NotFoundPage />
            </FormLayout>
          }
        />

        <Route
          path="/technical-error"
          element={
            <FormLayout>
              <TechnicalErrorPage />
            </FormLayout>
          }
        />

        <Route
          path="*"
          element={
            <FormLayout>
              <NotFoundPage />
            </FormLayout>
          }
        />

      </Routes>
    </MainLayout>
  );
};

export default App;
